// One dimension card, rendered in one of three visual states.
//
//   - grid: 3x2 default layout. Label, value, delta text, bullet/delta bar.
//   - strip: compressed mini-card on the top row when another dimension
//     is expanded. Smaller label and value; smaller bullet bar; no chart.
//   - expanded: the full-width card with the dimension's chart.
//
// Motion's `layout` prop on the outer m.div animates position/size as the
// CSS grid template changes. AnimatePresence inside the card cross-fades
// the chart vs the bullet bar — we never animate the ECharts canvas itself
// because it doesn't redraw while transformed.

import { AnimatePresence, m } from "motion/react";
import type { Dimension } from "../lib/dimensions";
import { deltaTextColour } from "../lib/dimensions";
import type { EngineOutput } from "../lib/types";
import { BulletDeltaBar } from "./bullet_delta_bar";
import { ChartCaveat } from "./chart_caveat";
import { DimensionChart } from "./charts/dimension_chart";

export type CardState = "grid" | "strip" | "expanded";

interface Props {
  dimension: Dimension;
  state: CardState;
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
  onExpand: () => void;
  onCollapse: () => void;
}

export function DimensionCard({
  dimension,
  state,
  engineOutput,
  baselineOutput,
  isModified,
  onExpand,
  onCollapse,
}: Props): JSX.Element {
  const value = engineOutput.summary_stats[dimension.key];
  const baseline =
    baselineOutput?.summary_stats[dimension.key] ?? value;
  const delta = value - baseline;
  const showDelta = isModified && Math.abs(delta) > 1e-6;
  const deltaColour = showDelta
    ? deltaTextColour(delta, dimension.goodWhen)
    : undefined;

  const clickHandler = state === "expanded" ? onCollapse : onExpand;
  // The expanded card shows the longer pageTitle; the box shows the short
  // label. The aria-label tracks whichever is *visible* so the accessible
  // name always contains the on-screen text (WCAG 2.5.3 Label in Name).
  const shownTitle =
    state === "expanded" ? (dimension.pageTitle ?? dimension.label) : dimension.label;
  const ariaLabel =
    state === "expanded" ? `Collapse ${shownTitle}` : `Expand ${shownTitle}`;

  return (
    <m.div
      layout
      layoutDependency={state}
      // Tween (not spring) so all six cards land on the same curve at the
      // same instant. Springs let each element overshoot independently,
      // which reads as visual noise when many morph in parallel. The cubic
      // bezier here is the "Apple standard" ease — eased out fast, gentle
      // arrival, no overshoot.
      transition={{
        layout: { duration: 0.5, ease: [0.32, 0.72, 0, 1] },
        default: { duration: 0.2 },
      }}
      className={`beamm-dimension-card beamm-dimension-card--${state}`}
      data-testid={`dimension-card-${dimension.key}`}
      data-state={state}
    >
      <button
        type="button"
        className="beamm-dimension-card-trigger"
        onClick={clickHandler}
        aria-label={ariaLabel}
        aria-expanded={state === "expanded"}
      >
        <m.span layout="position" className="beamm-dimension-card-label">
          {shownTitle}
        </m.span>
        {state !== "expanded" && (
          <m.span layout="position" className="beamm-dimension-card-value">
            {dimension.formatter(value)}
          </m.span>
        )}
        {showDelta && state === "grid" && (
          <span
            className="beamm-dimension-card-delta"
            data-testid="dimension-card-delta"
            style={{ color: deltaColour }}
          >
            {delta > 0 ? "+" : "−"}
            {dimension.formatter(Math.abs(delta))} vs baseline
          </span>
        )}
      </button>

      <AnimatePresence mode="wait" initial={false}>
        {state === "expanded" ? (
          <m.div
            key="chart"
            className="beamm-dimension-card-body"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.3 } }}
            exit={{ opacity: 0 }}
          >
            <DimensionChart
              dimensionKey={dimension.key}
              engineOutput={engineOutput}
              baselineOutput={baselineOutput}
              isModified={isModified}
            />
            <ChartCaveat dimension={dimension} />
          </m.div>
        ) : (
          <m.div
            key="bullet"
            className="beamm-dimension-card-bullet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <BulletDeltaBar
              dimension={dimension}
              value={value}
              baseline={baseline}
              isModified={isModified}
              size={state === "strip" ? "strip" : "grid"}
            />
          </m.div>
        )}
      </AnimatePresence>
    </m.div>
  );
}
